<template>
  <tr v-bind:class="{'tw:bg-amber-50' : selected}">
    <td v-if="selectable" class="d-print-none tw:w-px tw:whitespace-nowrap tw:align-middle">
      <input type="checkbox"
             :checked="selected"
             @change="updateSelection($event.target.checked)"
      />
    </td>
    <template v-if="showColumn('image')">
      <td width="75" class="tw:align-middle d-none d-print-table-cell d-lg-table-cell" v-on:click="showDetails = true">
        <img :src="equipment.product.image" :alt="equipment.product.name" width="50" class="img-fluid">
      </td>
    </template>
    <td v-if="showColumn('barcode')" class="tw:align-middle">
      <template v-if="isEditing">
        <div class="tw:inline-flex tw:items-center tw:gap-x-1">
          <input
            ref="barcodeInput"
            v-model="barcodeValue"
            type="text"
            class="tw:border tw:rounded tw:px-2 tw:py-0.5 tw:text-sm tw:font-mono tw:w-36 tw:focus:outline-none tw:focus:ring-1"
            :class="hasError ? 'tw:border-red-400 tw:focus:ring-red-300' : 'tw:border-zinc-300 tw:focus:ring-zinc-400'"
            :placeholder="$t('no barcode')"
            @keydown.enter.prevent="saveBarcode"
            @keydown.escape="resetBarcode"
            @blur="saveBarcode"
          />
          <button
            type="button"
            :title="$t('Cancel')"
            class="tw:text-zinc-400 tw:hover:text-zinc-700 tw:transition-colors"
            @mousedown.prevent="resetBarcode"
          >
            <XIcon class="tw:size-4" />
          </button>
        </div>
      </template>
      <template v-else>
        <div class="tw:group tw:inline-flex tw:relative tw:items-center">
          <a :href="equipment.links.show" class="tw:flex tw:items-center tw:gap-x-1">
            <QrCodeIcon class="tw:size-4" />
            <code v-if="localBarcodeNumber" class="tw:font-bold">{{ localBarcodeNumber }}</code>
            <span v-else class="text-muted">{{ $t('no barcode') }}</span>
          </a>
          <button
            type="button"
            :title="$t('Edit')"
            class="tw:absolute tw:left-full tw:ml-1.5 tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity tw:text-zinc-400 tw:hover:text-zinc-700"
            @click.prevent="startEditing"
          >
            <PenIcon class="tw:size-3" />
          </button>
        </div>
      </template>
    </td>
    <td v-if="showColumn('brand')" class="tw:align-middle">
      {{ equipment.product.brand.name }}
    </td>
    <template v-if="showColumn('model')">
      <td class="tw:align-middle">
        <p class="mb-0">
          <a :href="equipment.product.links.show">{{ equipment.product.name }}</a>
        </p>
        <small class="text-muted">
          {{ equipment.product.category }}
        </small>
      </td>
    </template>
    <td v-if="showColumn('year')" class="tw:align-middle">
      {{ equipment.product.year }}
    </td>
    <template v-if="showColumn('product-metafields')">
      <td v-for="productMetafield in productMetafields" class="tw:align-middle d-none d-lg-table-cell">
        {{ equipment.product.metafields.find( (m) => m.id === productMetafield.id)?.value }}
      </td>
    </template>
    <td v-if="showColumn('size-color')" class="tw:align-middle">
      {{ equipment.size }}<template v-if="equipment.size && equipment.color"> - </template>{{ equipment.color }}
    </td>
    <td v-if="showColumn('references')" class="d-none d-md-table-cell tw:align-middle">
      {{ equipment.serial_number }}
      <template v-if="equipment.reference && equipment.serial_number"><br></template>
      {{ equipment.reference }}
    </td>
    <template v-if="showColumn('equipment-metafields')">
      <td v-for="equipmentMetafield in equipmentMetafields" class="d-none d-lg-table-cell">
        {{ equipment.metafields.find( (m) => m.id === equipmentMetafield.id)?.value }}
      </td>
    </template>
    <td v-if="showColumn('condition')" class="d-none d-md-table-cell tw:align-middle">
      <template v-if="equipment.health_state">
        <span class="tw:flex tw:items-center tw:gap-x-2 tw:sm:justify-start">
          <EquipmentHealthState :state="equipment.health_state"></EquipmentHealthState>
          <span class="tw:block tw:text-sm tw:text-zinc-500">
            {{ equipment.health_state.label }}
          </span>
        </span>
      </template>
    </td>
    <td v-if="showColumn('status')" class="d-none d-md-table-cell tw:align-middle">
      <template v-if="equipment.current_status">
        <EquipmentStatus :status="equipment.current_status"></EquipmentStatus>
      </template>
    </td>
    <td v-if="showColumn('parks')" class="d-none d-sm-table-cell tw:text-zinc-600 tw:align-middle">
      <Tooltip :title="equipment.parks?.map(park => park.name).join(', ')">
        <span :class="[
          'tw:decoration-zinc-400 tw:underline tw:decoration-dotted tw:underline-offset-2',
          (equipment.parks?.length || 0) === 0 ? 'tw:text-red-500' : '',
        ]">
          {{ $t('park_count', {count: (equipment.parks?.length || 0)}, (equipment.parks?.length || 0)) }}
        </span>
      </Tooltip>
    </td>
    <td v-if="showColumn('tests')" class="d-none d-sm-table-cell tw:text-zinc-500 tw:align-middle">
      <Tooltip :title="secondsToDuration(equipment.tests_duration)">
        <span class="tw:decoration-zinc-400 tw:underline tw:decoration-dotted tw:underline-offset-2">
          {{ $t('tests_count', {count: equipment.tests_count}, equipment.tests_count) }}
        </span>
      </Tooltip>
    </td>
    <td v-if="showColumn('test-status')" class="d-print-none tw:align-middle tw:w-px tw:whitespace-nowrap">
      <TestStatus :in-progress="equipment.tests_in_progress" class="pr-2"></TestStatus>
    </td>
    <td v-if="showColumn('actions')" class="d-print-none tw:align-middle tw:w-px tw:whitespace-nowrap">
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button variant="outline">
            {{ $t('Actions') }}
            <ChevronDownIcon class="tw:ml-2 tw:h-4 tw:w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <template v-if="equipment.links.edit">
            <DropdownMenuItem as-child>
              <a :href="equipment.links.edit">
              <PenIcon class="tw:size-4" />
              {{ $t('Edit') }}
              </a>
            </DropdownMenuItem>
          </template>
          <template v-if="equipment.links.copy">
            <DropdownMenuItem as-child>
              <a :href="equipment.links.copy">
                <CopyIcon class="tw:size-4" />
                {{ $t('Copy') }}
              </a>
            </DropdownMenuItem>
          </template>
        </DropdownMenuContent>
      </DropdownMenu>
    </td>
  </tr>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed, watch, nextTick, inject, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import EquipmentStatus from "./EquipmentStatus.vue";
import TestStatus from "./TestStatus.vue";
import {
  ChevronDownIcon,
  CopyIcon,
  QrCodeIcon,
  PenIcon,
  XIcon,
} from "@lucide/vue";
import Tooltip from '@/components/ui/Tooltip.vue'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/shadcn/components/ui/dropdown-menu/index.js";
import { Button } from "@/shadcn/components/ui/button/index.js";
import { secondsToDuration } from "@/utils/date.js";
import { toast } from "vue-sonner";
import EquipmentHealthState from "@/components/equipment/EquipmentHealthState.vue";
import Equipment from "@/models/Equipment.js";

const { t } = useI18n();

const props = defineProps({
  equipment: {
    type: Object,
  },
  selectable: {
    type: Boolean,
    default: false,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  productMetafields: {
    type: Array,
    default: () => []
  },
  equipmentMetafields: {
    type: Array,
    default: () => []
  },
  columns: {
    type: Array,
    default: () => [],
  },
});

const showColumn = (name) => props.columns.length === 0 || props.columns.includes(name);

const emit = defineEmits(['selected', 'deselected']);

const selected = ref(false);
const showDetails = ref(false);

watch(() => props.isSelected, (val) => {
  selected.value = val;
});

const updateSelection = (e) => {
  selected.value = e;
  if (e) {
    emit('selected');
  } else {
    emit('deselected');
  }
};

// barcode inline editing
const editingEquipmentId = inject('editingEquipmentId', ref(null));
const isEditing = computed(() => editingEquipmentId.value === props.equipment.id);

const barcodeInput = ref(null);
const localBarcodeNumber = ref(props.equipment.barcode_number ?? '');
const barcodeValue = ref(props.equipment.barcode_number ?? '');
const hasError = ref(false);
const isSaving = ref(false);

const startEditing = async () => {
  barcodeValue.value = localBarcodeNumber.value;
  hasError.value = false;
  editingEquipmentId.value = props.equipment.id;
  await nextTick();
  barcodeInput.value?.focus();
};

const resetBarcode = () => {
  barcodeValue.value = localBarcodeNumber.value;
  editingEquipmentId.value = null;
};

const saveBarcode = async () => {
  if (isSaving.value) return;
  if (barcodeValue.value === localBarcodeNumber.value) {
    editingEquipmentId.value = null;
    return;
  }

  isSaving.value = true;

  try {
    const equipmentModel = new Equipment({
      id: props.equipment.id,
      barcode_number: barcodeValue.value || null,
    });
    const saved = await equipmentModel.save();
    localBarcodeNumber.value = saved.barcode_number ?? '';
    barcodeValue.value = localBarcodeNumber.value;
    editingEquipmentId.value = null;
    toast.success(t('Barcode updated'));
  } catch (err) {
    const message = err.response?.data?.message?.barcode_number?.[0]
      ?? err.response?.data?.message
      ?? t('Error');
    toast.error(message);
    hasError.value = true;
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => {
  selected.value = props.isSelected;
});
</script>